import { Card, CardContent } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Plus } from "lucide-react";

const gridCols = [
  "grid-cols-1",
  "grid-cols-2",
  "grid-cols-3",
  "grid-cols-4",
  "grid-cols-5",
];

export const AccessRequiredCard = () => {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[2px]">
      <span className="text-white font-semibold text-lg">Access Required</span>
    </div>
  );
};

const ActivityCard = ({
  isCreate,
  isSummary,
  data,
  onCardClick,
  isValueObject,
  isTitleOnly,
  hideCount = false,
  access = true,
}: {
  isCreate: boolean;
  isSummary: boolean;
  isValueObject?: boolean;
  isTitleOnly?: boolean;
  hideCount?: boolean;
  data: any;
  onCardClick?: () => void;
  access?: boolean;
}) => {
  if (isCreate) {
    return (
      <Card
        className={cn(
          "relative bg-primary-300 border-0 shadow-none shadow-md hover:shadow-lg transition-all overflow-hidden",
          access ? "cursor-pointer" : "cursor-not-allowed"
        )}
        onClick={() => {
          access && onCardClick?.();
        }}
      >
        <CardContent className="flex flex-col items-center justify-center h-[100%]">
          <Plus className="h-10 w-10 stroke-primary" />
          <span className="text-primary text-[16px] leading-[18px] font-[500] text-center">
            {data?.title || ""}
          </span>
        </CardContent>
        {!access && <AccessRequiredCard />}
      </Card>
    );
  } else if (isTitleOnly) {
    return (
      <Card
        className={cn(
          "relative overflow-hidden bg-primary-300 border-0 shadow-none shadow-md cursor-pointer hover:shadow-lg transition-all min-h-[150px]",
          access ? "cursor-pointer" : "cursor-not-allowed"
        )}
        onClick={() => {
          access && onCardClick?.();
        }}
      >
        <CardContent className="flex flex-col items-center justify-center h-[100%]">
          <span className="text-primary text-[16px] leading-[18px] font-[500]">
            {data?.title || ""}
          </span>
        </CardContent>
        {!access && <AccessRequiredCard />}
      </Card>
    );
  } else if (isSummary) {
    return (
      <Card
        className={cn(
          "relative bg-primary-300 border-0 min-h-[180px] shadow-none shadow-md cursor-pointer hover:shadow-lg transition-all p-0 m-0 overflow-hidden",
          access ? "cursor-pointer" : "cursor-not-allowed"
        )}
        onClick={() => {
          access && onCardClick?.();
        }}
      >
        <span
          className={cn(
            "absolute w-full text-right p-3 pt-2 tex-[14px] font-[400]",
            data?.subtitleColor
          )}
        >
          {data?.subtitle}
        </span>
        <CardContent className="flex flex-row p-5 pt-8 text-primary gap-3">
          <div className="flex flex-col justify-between">
            {data?.data?.map((val: any, index: string) => (
              <span key={`key:${index}`}>
                {val.label}
              </span>
            ))}
          </div>
          <div className="flex flex-col justify-between">
            {data?.data?.map((val: any, index: string) => {
              return (
                <span key={`value:${index}`}>
                  : {val.value}
                </span>
              );
            })}
          </div>
        </CardContent>
        <span className="absolute bottom-0 left-0 right-0 text-white text-[12px] lg:text-[16px] lg:leading-[18px] font-[500] bg-primary px-3 py-2 text-center">
          {data?.title}
        </span>
        {!access && <AccessRequiredCard />}
      </Card>
    );
  } else if (isValueObject) {
    const idx = data?.dataHeader?.findIndex((val: any) => val == " ");
    if (idx == -1) {
      data?.dataHeader?.splice(1, 0, " ");
    }

    return (
      <Card
        className={cn(
          "relative bg-primary-300 border-0 min-h-[180px] shadow-none shadow-md cursor-pointer hover:shadow-lg transition-all p-0 m-0 overflow-hidden",
          access ? "cursor-pointer" : "cursor-not-allowed"
        )}
        onClick={() => {
          access && onCardClick?.();
        }}
      >
        <span
          className={cn(
            "absolute w-full text-right p-3 pt-2 tex-[14px] font-[400]",
            data?.subtitleColor
          )}
        >
          {data?.subtitle}
        </span>
        <CardContent className="flex flex-row p-5 pt-8 text-primary">
          <div
            className={cn(
              "grid justify-between",
              gridCols[data?.dataHeader?.length]
            )}
          >
            {data?.dataHeader?.map((val: any, index: number) => {
              return (
                <span
                  key={`key:${index}`}
                  className={cn(
                    index == 0 ? "col-span-2" : "",
                    "w-full text-center text-black text-[12px] mb-2"
                  )}
                >
                  {val}
                </span>
              );
            })}
            {data?.data?.map((val: any, index: number) => {
              return (
                <>
                  <div key={`key:${index}`} className={cn("col-span-2 w-full")}>
                    <span>{val.label}</span>
                  </div>
                  :
                  {val?.value?.map((v: any, i: number) => {
                    return (
                      <span key={`value-${index}-${i}`} className="text-center">
                        {v}
                      </span>
                    );
                  })}
                </>
              );
            })}
          </div>
        </CardContent>
        <span className="absolute bottom-0 left-0 right-0 text-white text-[12px] lg:text-[16px] lg:leading-[18px] font-[500] bg-primary px-3 py-2 text-center">
          {data?.title}
        </span>
        {!access && <AccessRequiredCard />}
      </Card>
    );
  } else {
    return (
      <Card
        className={cn(
          "relative bg-primary-300 border-0 min-h-[180px] shadow-none shadow-md cursor-pointer hover:shadow-lg transition-all p-0 m-0 overflow-hidden",
          access ? "cursor-pointer" : "cursor-not-allowed"
        )}
        onClick={() => {
          access && onCardClick?.();
        }}
      >
        <span
          className={cn(
            "absolute w-full text-right p-3 pt-2 tex-[14px] font-[400]",
            data?.subtitleColor
          )}
        >
          {data?.subtitle}
        </span>
        <CardContent className="flex flex-row p-5 pt-8 text-primary">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[20px] items-center justify-between">
            <div className="flex flex-col justify-between pb-10 order-2 lg:order-1">
              {hideCount ? (
                <div className="h-[34px]"></div>
              ) : (
                <span className="text-[34px] font-[600]">{data?.count}</span>
              )}
              <span>{data?.desc}</span>
            </div>
            <div className="order-1 lg:order-2 lg:flex lg:justify-end">
              {data.icon ? (
                <data.icon className="h-10 w-10 stroke-primary" />
              ) : (
                <></>
              )}
            </div>
          </div>
        </CardContent>
        <span className="absolute bottom-0 left-0 right-0 text-white text-[12px] lg:text-[16px] lg:leading-[18px] font-[500] bg-primary px-3 py-2 text-center">
          {data?.title}
        </span>
        {!access && <AccessRequiredCard />}
      </Card>
    );
  }
};

export default ActivityCard;
// Fetch Billing Category Based on ModelId
